import type { ItemCard as ItemCardType } from '@/types/dashboard'
import { useItemsBoardStore } from '@/stores/items-board-store'
import { X, Check, ArrowRight, ArrowLeft } from 'lucide-react'

interface ItemCardProps {
  item: ItemCardType
}

const priorityConfig = {
  money: {
    number: '1',
    color: '#63d706',
  },
  growth: {
    number: '2',
    color: '#3b82f6',
  },
  critical: {
    number: '3',
    color: '#d70606',
  },
  backlog: {
    number: '',
    color: '#6b7280',
  },
}

export function ItemCard({ item }: ItemCardProps) {
  const { removeItem, toggleComplete, moveToStatus } = useItemsBoardStore()

  const priorities = Array.isArray(item.priority) ? item.priority : [item.priority]

  const canMoveToBacklog = item.status === 'current'
  const canMoveToCurrent = item.status === 'future'

  return (
    <div className={`py-2 px-1 transition-all hover:bg-white/[0.02] group border-b border-white/[0.05] ${item.completed ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-2.5">
        {(item.status === 'last-week' || item.status === 'current') && (
          <button
            onClick={() => toggleComplete(item.id)}
            className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors mt-0.5 ${
              item.completed ? 'bg-white/20 border-white/40' : 'border-white/20 hover:border-white/40'
            }`}
          >
            {item.completed && <Check className="w-2.5 h-2.5 text-white" />}
          </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-baseline gap-2 flex-1">
              <h3 className={`font-medium text-white text-sm leading-snug ${item.completed ? 'line-through text-white/50' : ''}`}>
                {item.title}
              </h3>
              {/* Priority indicators - just numbers */}
              <div className="flex gap-0.5">
                {priorities.map((priority) => {
                  const config = priorityConfig[priority]
                  if (!config.number) return null
                  return (
                    <span key={priority} className="text-xs font-semibold opacity-60" style={{ color: config.color }}>
                      {config.number}
                    </span>
                  )
                })}
              </div>
            </div>

            <div className="flex items-center gap-0.5">
              {canMoveToBacklog && (
                <button
                  onClick={() => moveToStatus(item.id, 'future')}
                  className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-white/70 transition-all p-0.5"
                  title="Move to Backlog"
                >
                  <ArrowRight className="w-3 h-3" />
                </button>
              )}
              {canMoveToCurrent && (
                <button
                  onClick={() => moveToStatus(item.id, 'current')}
                  className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-white/70 transition-all p-0.5"
                  title="Move to This Week"
                >
                  <ArrowLeft className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => removeItem(item.id)}
                className="text-white/30 hover:text-white/70 transition-colors p-0.5"
                aria-label="Remove item"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {item.description && (
            <p className={`text-xs leading-relaxed ${item.completed ? 'text-white/30' : 'text-white/50'}`}>
              {item.description}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
